// account-auth — MOTOR de autenticação da CONTA SWIPE (email/senha + Google).
//
// É a lógica PURA de auth, separada da apresentação: as telas (mobile na parede
// A1; futuramente o login-na-TV I8) consomem estas funções e não falam direto com
// o backend. Cada função:
//   1. monta o request (com device meta),
//   2. chama o endpoint /v1/swipe-account/{register,login,oauth/google},
//   3. em sucesso, PERSISTE a sessão Swipe (loginSwipeSession) pra o sync e a
//      identidade da conta funcionarem (igual o pareamento QR faz), e
//   4. devolve { ok, user } ou { ok:false, error } com um CÓDIGO de erro estável.
//
// POR QUE não reusar o `request()` do api.ts: aquele wrapper é do token XTREAM
// (faz clearTokens/notifyAuthInvalid em 401, failover de endpoints). Aqui o 401 é
// "credencial inválida", não "sessão Xtream expirou" — não pode disparar logout
// global. Então falamos direto com o fetch + getApiBase (mesmo padrão do
// swipe-session.ts/refresh), sem efeito colateral no auth-store Xtream.
//
// ISOLAMENTO: motor PURO (sem React, sem Norigin). Roda em qualquer superfície.

import { getApiBase } from '../api'
import { getDeviceInfo } from '../fingerprint'
import { loginSwipeSession } from '../sync'

/** Conta Swipe autenticada (espelha o `user` do backend). */
export interface AccountUser {
  id: string
  email: string
  name: string | null
}

/** Códigos de erro estáveis que a UI traduz pra copy. `unknown_error` é o
 *  catch-all. Os de rede/timeout/servidor são genéricos (a UI mostra "tente de
 *  novo"); os de credencial são específicos (copy direcionada). */
export type AuthErrorCode =
  | 'invalid_credentials'
  | 'use_google_login'
  | 'email_already_registered'
  | 'account_suspended'
  | 'google_email_not_verified'
  | 'invalid_google_token'
  | 'network_error'
  | 'server_error'
  | 'unknown_error'

export type AuthResult =
  | { ok: true; user: AccountUser }
  | { ok: false; error: AuthErrorCode }

/** Resposta de sucesso dos 3 endpoints de auth (mesma forma). */
interface AuthSuccessBody {
  accessToken: string
  refreshToken: string
  user: { id: string; email: string; name: string | null }
}

/** Mapeia o `error` do backend (4xx) pro código estável da UI. Default por status
 *  pra erros não enumerados (5xx → server_error; resto → unknown_error). */
function mapBackendError(status: number, backendCode: string | undefined): AuthErrorCode {
  switch (backendCode) {
    case 'invalid_credentials':
      return 'invalid_credentials'
    case 'use_google_login':
      return 'use_google_login'
    case 'email_already_registered':
      return 'email_already_registered'
    case 'account_suspended':
      return 'account_suspended'
    case 'google_email_not_verified':
      return 'google_email_not_verified'
    case 'invalid_google_token':
    case 'incomplete_google_payload':
      return 'invalid_google_token'
    default:
      if (status >= 500) return 'server_error'
      return 'unknown_error'
  }
}

/**
 * POST genérico pros 3 endpoints de auth Swipe. Em sucesso (2xx) persiste a
 * sessão via loginSwipeSession (mesmo ponto canônico do QR — limpa o outbox se a
 * conta mudou) e devolve o user. Erros: REDE (fetch lança) → network_error;
 * sessão não persistiu (token sem swipeUserId, raro) → server_error.
 */
async function postAuth(path: string, body: unknown): Promise<AuthResult> {
  let res: Response
  try {
    res = await fetch(`${getApiBase()}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    // DNS/conn refused/timeout de rede → erro transitório, NÃO desloga nada.
    return { ok: false, error: 'network_error' }
  }

  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string }
    return { ok: false, error: mapBackendError(res.status, data.error) }
  }

  let parsed: AuthSuccessBody
  try {
    parsed = (await res.json()) as AuthSuccessBody
  } catch {
    return { ok: false, error: 'server_error' }
  }
  if (!parsed.accessToken || !parsed.refreshToken || !parsed.user?.id) {
    return { ok: false, error: 'server_error' }
  }

  // Persiste a sessão Swipe (igual ao pareamento). Se o token não trouxer
  // swipeUserId (não deveria), loginSwipeSession devolve false → tratamos como
  // erro de servidor (sem sessão o app não funciona logado).
  const persisted = await loginSwipeSession(parsed.accessToken, parsed.refreshToken)
  if (!persisted) return { ok: false, error: 'server_error' }

  return {
    ok: true,
    user: { id: parsed.user.id, email: parsed.user.email, name: parsed.user.name ?? null },
  }
}

/** Login com email + senha. Persiste a sessão Swipe em sucesso. */
export async function loginWithEmail(email: string, password: string): Promise<AuthResult> {
  const device = await getDeviceInfo()
  return postAuth('/v1/swipe-account/login', {
    email: email.trim(),
    password,
    device,
  })
}

/**
 * Cadastro com email + senha (+ nome opcional). Em sucesso já entra logado
 * (o backend devolve tokens no register, igual ao login). Persiste a sessão.
 */
export async function registerWithEmail(
  email: string,
  password: string,
  name?: string,
): Promise<AuthResult> {
  const device = await getDeviceInfo()
  const trimmedName = name?.trim()
  return postAuth('/v1/swipe-account/register', {
    email: email.trim(),
    password,
    ...(trimmedName ? { name: trimmedName } : {}),
    device,
  })
}

/**
 * Login/cadastro via Google. Recebe o `idToken` já obtido do Google Identity
 * Services no cliente (ver mobile/google-signin.ts) e o manda pro backend
 * validar (verifyIdToken). Persiste a sessão em sucesso. O backend faz o
 * link/criação da conta automaticamente.
 */
export async function loginWithGoogle(idToken: string): Promise<AuthResult> {
  const device = await getDeviceInfo()
  return postAuth('/v1/swipe-account/oauth/google', {
    idToken,
    device,
  })
}

// ===== Reset de senha (código por email) =====
//
// Fluxo em 2 passos (formato CÓDIGO, não link — app encapsulado sem deep-link):
//   1. requestPasswordReset(email): backend SEMPRE responde 200 genérico
//      (anti-enumeração). A UI mostra a mesma mensagem exista ou não a conta.
//   2. confirmPasswordReset(code, newPassword): valida o código e troca a senha.
//      NÃO loga automaticamente (o reset revoga as sessões no backend) — a UI
//      manda o usuário voltar pro login e entrar com a senha nova.

/** Resultado genérico de um passo do reset. `network_error`/`server_error` são
 *  transitórios (UI mostra "tente de novo"); `invalid_code` é específico. */
export type ResetErrorCode = 'invalid_code' | 'network_error' | 'server_error' | 'unknown_error'

export type ResetResult = { ok: true } | { ok: false; error: ResetErrorCode }

/**
 * Passo 1: pede o envio do código de reset pro email. SEMPRE trata 2xx como
 * sucesso (o backend não revela se o email existe). Só erros de rede/servidor
 * viram falha — a UI mostra a mensagem genérica de "verifique seu email".
 */
export async function requestPasswordReset(email: string): Promise<ResetResult> {
  let res: Response
  try {
    res = await fetch(`${getApiBase()}/v1/swipe-account/password/forgot`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: email.trim() }),
    })
  } catch {
    return { ok: false, error: 'network_error' }
  }
  if (res.ok) return { ok: true }
  if (res.status >= 500) return { ok: false, error: 'server_error' }
  // 4xx aqui é raro (só validação de email malformado) — genérico.
  return { ok: false, error: 'unknown_error' }
}

/**
 * Passo 2: confirma o reset com o código + nova senha. Em sucesso, a senha já
 * foi trocada e as sessões antigas revogadas no backend; a UI volta pro login.
 * Código inválido/expirado/usado → `invalid_code` (mensagem direcionada).
 */
export async function confirmPasswordReset(
  code: string,
  newPassword: string,
): Promise<ResetResult> {
  let res: Response
  try {
    res = await fetch(`${getApiBase()}/v1/swipe-account/password/reset`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code: code.trim(), newPassword }),
    })
  } catch {
    return { ok: false, error: 'network_error' }
  }
  if (res.ok) return { ok: true }
  if (res.status >= 500) return { ok: false, error: 'server_error' }
  const data = (await res.json().catch(() => ({}))) as { error?: string }
  if (data.error === 'invalid_or_expired_code') return { ok: false, error: 'invalid_code' }
  return { ok: false, error: 'unknown_error' }
}
